import { createLogger } from './logger.js';

const recaptchaLogger = createLogger('RecaptchaService');

/**
 * Service for handling reCAPTCHA Enterprise verification
 */
class RecaptchaEnterpriseService {
  constructor() {
    this.isLoaded = false;
    this.isLoading = false;
    this.loadPromise = null;
  }

  async loadScript(siteKey) {
    if (this.isLoaded) return true;
    if (this.isLoading) return this.loadPromise;

    this.isLoading = true;
    this.loadPromise = this._loadRecaptchaScript(siteKey);
    
    try {
      await this.loadPromise;
      this.isLoaded = true;
      return true;
    } catch (error) {
      this.isLoading = false;
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  _loadRecaptchaScript(siteKey) {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.grecaptcha?.enterprise) {
        resolve();
        return;
      }

      // Remove any duplicate fido2 scripts that might cause conflicts
      const fidoScripts = document.querySelectorAll('script[id*="fido2"]');
      fidoScripts.forEach(script => {
        recaptchaLogger.debug('Removing duplicate fido2 script', { scriptId: script.id });
        script.remove();
      });
      // Check if script is already in DOM
      const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
      if (existingScript) {
        existingScript.onload = resolve;
        existingScript.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        // Wait for grecaptcha to be ready
        if (window.grecaptcha?.enterprise) {
          window.grecaptcha.enterprise.ready(resolve);
        } else {
          reject(new Error('reCAPTCHA Enterprise not available'));
        }
      };

      script.onerror = () => {
        reject(new Error('Failed to load reCAPTCHA Enterprise script'));
      };

      document.head.appendChild(script);

      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error('reCAPTCHA loading timeout'));
      }, 10000);
    });
  }

  async executeRecaptcha(siteKey, action = 'submit') {
    if (!this.isLoaded || !window.grecaptcha?.enterprise) {
      throw new Error('reCAPTCHA Enterprise not loaded');
    }

    try {
      const token = await window.grecaptcha.enterprise.execute(siteKey, { action });
      if (!token) {
        throw new Error('Failed to generate reCAPTCHA token');
      }
      return token;
    } catch (error) {
      throw new Error(`reCAPTCHA execution failed: ${error.message}`);
    }
  }

  /**
   * Verify reCAPTCHA token using Supabase Edge Function
   * @param {string} token - reCAPTCHA token
   * @param {string} action - Expected action
   * @returns {Promise<{success: boolean, score?: number, error?: string}>}
   */
  async verifyToken(token, action = 'register') {
    try {
      recaptchaLogger.debug('reCAPTCHA verification started', {
        action,
        tokenLength: token?.length
      });

      const { data, error } = await supabase.functions.invoke('verify-recaptcha', {
        body: {
          token,
          action
        }
      });

      if (error) {
        recaptchaLogger.error('reCAPTCHA verification error', {
          message: error.message,
          context: error.context,
          details: error
        });
        
        // Check for specific error types
        if (error.message?.includes('not configured')) {
          return {
            success: false,
            error: 'reCAPTCHA service is not properly configured. Please contact support.'
          };
        }
        
        return {
          success: false,
          error: error.message || 'Failed to verify reCAPTCHA'
        };
      }

      recaptchaLogger.debug('reCAPTCHA verification response received', data);
      return data;
    } catch (error) {
      recaptchaLogger.error('reCAPTCHA service error', error);
      return {
        success: false,
        error: 'reCAPTCHA verification service unavailable'
      };
    }
  }

  /**
   * Execute reCAPTCHA Enterprise and verify token
   * @param {string} siteKey - reCAPTCHA site key
   * @param {string} action - Action name
   * @returns {Promise<{success: boolean, token?: string, score?: number, error?: string}>}
   */
  async executeAndVerify(siteKey, action = 'register') {
    try {
      // Execute reCAPTCHA to get token
      const token = await this.executeRecaptcha(siteKey, action);
      
      // Verify token with backend
      const verification = await this.verifyToken(token, action);
      
      return {
        success: true,
        token,
        score: verification.score,
        action: verification.action
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        token: null,
        score: null
      };
    }
  }

  reset() {
    // reCAPTCHA Enterprise doesn't require explicit reset
    console.log('reCAPTCHA Enterprise reset requested');
  }


}

// Export singleton instance
const recaptchaEnterpriseService = new RecaptchaEnterpriseService();
export default recaptchaEnterpriseService;
